import React, { useRef, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { Trans, useTranslation } from 'react-i18next';
import 'components/styles/main-form.css';
import 'components/styles/otp-input.css';

import { useOtp } from '../hooks/useOtp';

import { ReactComponent as BackArrow } from 'assets/images/back-arrow.svg';
import { ReactComponent as EmailResendIcon } from 'assets/images/email-resend.svg';

const OtpScreen = ({
  partnerId,
  email,
  mode,
  cardNumber,
  otpUuid,
  setOtpUuid,
  onSuccess,
  onBack,
  onChangeEmail,
  countryOfOperation
}) => {
  const inputs = useRef([]);
  const { otpValue, setOtpValue, otpError, isSubmitting, handleOtpSubmit, handleOtpResend } =
    useOtp({ partnerId, email, mode, cardNumber, otpUuid, setOtpUuid, countryOfOperation });
  const [timer, setTimer] = useState(30);

  useEffect(() => {
    if (timer > 0) {
      const interval = setInterval(() => {
        setTimer((prev) => prev - 1);
      }, 1000);
      return () => clearInterval(interval);
    }
  }, [timer]);

  const handleResend = async () => {
    if (timer === 0) {
      setTimer(30);
      await handleOtpResend();
    }
  };

  const otpDigits = (otpValue || '').padEnd(6, '').split('');

  const handleOtpInput = (e, idx) => {
    const val = e.target.value.replace(/\D/g, '');
    if (!val) return;
    let newOtp = otpDigits.slice();
    newOtp[idx] = val[val.length - 1];
    setOtpValue(newOtp.join(''));
    if (idx < 5 && inputs.current[idx + 1]) {
      inputs.current[idx + 1].focus();
    }
  };

  const handleKeyDown = (e, idx) => {
    if (e.key === 'Backspace' || e.key === 'Delete') {
      let newOtp = otpDigits.slice();
      if (otpDigits[idx]) {
        newOtp[idx] = '';
        setOtpValue(newOtp.join(''));
      } else if (idx > 0) {
        if (inputs.current[idx - 1]) {
          inputs.current[idx - 1].focus();
        }
        newOtp[idx - 1] = '';
        setOtpValue(newOtp.join(''));
      }
      e.preventDefault();
    }
  };
  const { t } = useTranslation();

  const maskEmail = (email) => {
    if (!email) return '';
    const [user, domain] = email.split('@');
    if (!user || !domain) return email;
    let maskedUser =
      user.length <= 2 ? user[0] + '*' : user[0] + '*'.repeat(user.length - 2) + user.slice(-1);
    return maskedUser + '@' + domain;
  };

  const handleBack = () => {
    setOtpValue('');
    if (onBack) onBack();
  };
  const handleChangeEmail = () => {
    setOtpValue('');
    if (onChangeEmail) onChangeEmail();
  };

  const isOtpValid = otpValue && otpValue.length === 6;

  return (
    <div className="linkAccount">
      <div className="header otp-header-row">
        <button className="otp-back-arrow" onClick={handleBack} aria-label="Back">
          <BackArrow width={24} height={24} />
        </button>
        {t('PARTNERSHIP_PORTAL.ENTER_OTP')}
      </div>
      <div className="form">
        <div className="otp-form-inner">
          <div className="otp-instruction-row">
            <span className="otp-instruction-text">
              <Trans
                i18nKey="PARTNERSHIP_PORTAL.ENTER_OTP_INSTRUCTION"
                values={{ email: maskEmail(email) }}
                components={{ b: <b /> }}
              />
            </span>
            <button className="blueColor otp-change-link" onClick={handleChangeEmail}>
              {t('PARTNERSHIP_PORTAL.CHANGE')}
            </button>
          </div>
          <div className="bottomRow otp-row">
            {Array.from({ length: 6 }).map((_, idx) => (
              <input
                key={idx}
                type="text"
                inputMode="numeric"
                pattern="[0-9]*"
                maxLength={1}
                ref={(el) => (inputs.current[idx] = el)}
                value={otpDigits[idx] || ''}
                onChange={(e) => handleOtpInput(e, idx)}
                onKeyDown={(e) => handleKeyDown(e, idx)}
                className="otp-input"
                autoFocus={idx === 0}
                aria-label={`OTP digit ${idx + 1}`}
              />
            ))}
          </div>
          <div className="otp-resend-timer-row">
            <span className="otp-resend-timer-text">{t('PARTNERSHIP_PORTAL.RESEND_IN')} </span>
            <span className="otp-resend-timer-count">{`00 : ${timer
              .toString()
              .padStart(2, '0')}`}</span>
          </div>
          <div className="resend-methods otp-resend-methods">
            <div className="text-md otp-resend-method-title">
              {t('PARTNERSHIP_PORTAL.RESEND_CODE_VIA')}
            </div>
            <div className="otp-resend-btn-row">
              <button
                className={`resend-btn otp-resend-btn${
                  timer !== 0 ? ' otp-resend-btn-disabled' : ''
                }`}
                disabled={timer !== 0}
                onClick={handleResend}>
                <span className="otp-resend-btn-icon">
                  <EmailResendIcon width={20} height={20} />
                </span>
                <span className="otp-resend-btn-label">{t('PARTNERSHIP_PORTAL.EMAIL')}</span>
              </button>
            </div>
          </div>
        </div>
        {otpError && <div className="checkBoxError otp-error-message">{otpError}</div>}
        <button
          onClick={() => handleOtpSubmit(onSuccess)}
          className={`submit otp-submit-btn${!isOtpValid || isSubmitting ? ' inactive' : ''}`}
          disabled={!isOtpValid || isSubmitting}>
          {t('PARTNERSHIP_PORTAL.LINK_YOUR_ACCOUNT')}
        </button>
      </div>
    </div>
  );
};

OtpScreen.propTypes = {
  partnerId: PropTypes.string,
  email: PropTypes.string,
  mode: PropTypes.string,
  cardNumber: PropTypes.string,
  otpUuid: PropTypes.string,
  setOtpUuid: PropTypes.func,
  onSuccess: PropTypes.func,
  onBack: PropTypes.func.isRequired,
  onChangeEmail: PropTypes.func,
  countryOfOperation: PropTypes.string
};

export default OtpScreen;
